import React from 'react'

import { Card, TableV2 as TableV2Component } from 'src/components'
import { useAppSelector } from 'src/store/hooks'
import { selectCompanyCountry } from 'src/selectors/company.selector'
import {
  ClientBalanceResponse,
  getClientIdentification,
} from 'src/utils/clientIdentification'
import {
  DEFAULT_PAGE_SIZE,
  FetchPage,
  useClientBalanceList,
} from './useClientBalanceList'

interface IProps {
  fetchPage: FetchPage
  extraParams?: object
  selectedId: number | null
  onSelect: (item: ClientBalanceResponse) => void
  renderValue: (item: ClientBalanceResponse, isActive: boolean) => React.ReactNode
  renderActions?: (item: ClientBalanceResponse) => React.ReactNode
  /** Título del Card contenedor. `null` omite el Card (el consumidor lo provee). */
  title?: string | null
  reloadKey?: number
}

/**
 * Panel "Clientes" paginado server-side, reusable (Payments CPC / Vouchers).
 * El endpoint entra por `fetchPage`; el área de saldos por `renderValue`.
 * Orden y búsqueda los resuelve el BE (spec ATL-306); sin filtro client-side.
 */
// TableV2 (PaginatedTableV2) es JS sin tipos; se relaja el tipo en el borde del
// import y se tipan solo las props que se usan (kolo-web/CLAUDE.md §8).
const TableV2 = TableV2Component as unknown as React.ComponentType<{
  loading?: boolean
  items: ClientBalanceResponse[]
  total: number
  renderRow: (item: ClientBalanceResponse) => React.ReactNode
  getPagination: (skip: number, size: number, search?: string) => void
  handleChange: (search: string) => void
}>

const ClientBalanceList = ({
  fetchPage,
  extraParams = {},
  selectedId,
  onSelect,
  renderValue,
  renderActions,
  title = 'Clientes',
  reloadKey = 0,
}: IProps) => {
  const country = useAppSelector(selectCompanyCountry)
  const { items, total, loading, load } = useClientBalanceList(
    fetchPage,
    extraParams,
    reloadKey,
  )

  const renderRow = (item: ClientBalanceResponse) => {
    const isActive = item.id === selectedId ? 'active' : ''
    return (
      <div
        className={`b-user-item-v2 ${isActive}`}
        key={item.id}
        onClick={() => onSelect(item)}>
        <div style={{ width: '100%' }} className={isActive}>
          <div className={`b-user-item ${isActive}`}>
            <div
              className={'justify-content-start align-items-start bu-en'}
              style={{ textAlign: 'left' }}>
              <div className={`b-user-name ${isActive}`}>{item.name}</div>
              <div className={`b-user-email ${isActive}`}>
                {getClientIdentification(item.identification, country?.id)}
              </div>
              <div className={`b-user-email ${isActive}`}>{item.code}</div>
            </div>
            {renderActions && (
              <div
                className={'d-flex row align-items-center mr-2 bu-b'}
                style={{ textAlign: 'right' }}
                onClick={e => e.stopPropagation()}>
                {renderActions(item)}
              </div>
            )}
          </div>
          {renderValue(item, item.id === selectedId)}
        </div>
      </div>
    )
  }

  const table = (
    <TableV2
      loading={loading}
      items={items}
      total={total}
      renderRow={renderRow}
      getPagination={(skip: number, size: number, search?: string) =>
        load(skip, size, search)
      }
      handleChange={(search: string) => load(0, DEFAULT_PAGE_SIZE, search)}
    />
  )

  if (title === null) return table
  return <Card title={title}>{table}</Card>
}

export default ClientBalanceList
